import { AppShell, Box, Group, ScrollArea } from '@mantine/core';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { Roles } from '@bmai/shared';
import { useAuth } from '@/features/auth/AuthContext';
import UserMenuButton from '@/shell/UserMenuButton';
import { UltraTraceWordmark } from '@/design/UltraTraceLogo';

/**
 * Minimal chrome for the first-run onboarding wizard: a top bar (logo + user
 * menu) and the wizard content only — no left sidebar, so the tenant admin sees
 * just the wizard until they finish or skip. Operators are bounced to their own
 * console like the main shell.
 */
export default function OnboardingLayout(): React.ReactElement {
    const { isLoading, hasRole, legal } = useAuth();
    const { pathname } = useLocation();

    if (!isLoading && hasRole(Roles.PLATFORM_OPERATOR)) {
        return <Navigate to="/operator" replace />;
    }

    if (legal?.needsAcceptance && pathname !== '/legal') {
        return <Navigate to="/legal" replace />;
    }

    return (
        <AppShell
            header={{ height: 56 }}
            padding={0}
            styles={{ main: { backgroundColor: 'var(--bm-color-canvas)', height: '100vh', overflow: 'hidden' } }}
        >
            <AppShell.Header style={{ backgroundColor: 'var(--bm-color-body)', borderBottom: '1px solid var(--bm-color-border)' }}>
                <Group h="100%" px="md" align="center" wrap="nowrap" justify="space-between">
                    <UltraTraceWordmark size={22} title="UltraTrace" />
                    <UserMenuButton />
                </Group>
            </AppShell.Header>

            <AppShell.Main>
                <ScrollArea h="calc(100vh - 56px)" type="hover" scrollbarSize={8}>
                    <Box p={{ base: 'md', sm: 'xl' }}>
                        <Box maw={780} mx="auto">
                            <Outlet />
                        </Box>
                    </Box>
                </ScrollArea>
            </AppShell.Main>
        </AppShell>
    );
}
